import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useT, useLanguage } from "../lib/LanguageContext";
import { formatDateTime } from "../lib/dateTime";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import LoadFailure from "../components/ui/LoadFailure";
import Pager from "../components/ui/Pager";
import TenantPicker from "./TenantPicker";
import { usePagedList } from "./usePagedList";
import { useOperator } from "./OperatorContext";
import { mayOpen, useSecondaryRead } from "./screenAccess";
import { tenantDisplayName } from "./tenantDisplay";
import { auditActionLabel, auditRoleLabel, auditTargetLabel } from "./auditActionLabels";
import { FILTER_MAX_LENGTH, normalizeSearch } from "./useDebouncedSearch";
import DateRangeFilter, { isRangeInvalid } from "./DateRangeFilter";
import { Loader2, History, Filter, RefreshCw } from "lucide-react";

const PAGE_SIZE = 25;
const NO_FILTERS = { actor: "", action: "", target_type: "", tenant_id: "", from: "", to: "" };
const SELECT_CLASS = "bg-white border border-slate-200 rounded-md px-3 py-2 text-sm text-slate-700";

const ACTION_ICONS = {
  "tenant.create": "🌱", "tenant.update": "✏️", "tenant.suspend": "⏸",
  "tenant.activate": "▶", "tenant.delete": "🗑",
  "tenant.subscriptions_cancelled": "⛔", "tenant.delete_blocked": "⚠",
  "tenant.delete_abandoned": "↺",
  "team.create": "👤+", "team.update": "👤✏️", "team.delete": "👤−",
  "billing.rebate": "💸", "billing.adjust": "🧮", "billing.refund": "↩️",
  "practice.enter": "🔑", "practice.write": "🛠",
};

// The options of a filter dropdown: the server's values verbatim, plus the drafted one when the
// server does not list it (facets failed, or its events were purged), so the control never
// misstates the active filter.
const withDrafted = (values, drafted) => (drafted && !values.includes(drafted) ? [...values, drafted] : values);

// Reader-facing verb of a practice.write row, by HTTP method (PUT and PATCH read the same).
// GET is a provider callback completing a connection the operator started (Google Calendar,
// PayPal, a social network): backend operator_entry.mark_flow_change, contract §3.5.
const METHOD_KEYS = {
  POST: "audit.methodPost", PUT: "audit.methodPut", PATCH: "audit.methodPatch",
  DELETE: "audit.methodDelete", GET: "audit.methodGet",
};

export default function AuditLogs() {
  const t = useT("superadmin");
  const { lang } = useLanguage();
  const operator = useOperator();
  const showPracticePicker = useSecondaryRead("tenantOptions");
  const [stats, setStats] = useState(null);
  // { actions: string[], target_types: string[] } | null; `facetsFailed` drives the inline note.
  const [facets, setFacets] = useState(null);
  const [facetsFailed, setFacetsFailed] = useState(false);
  // `draft` is what the inputs show; `applied` is what the last Apply committed and what
  // the read effect sends. Typing alone never reads, and a commit always restarts at page 1.
  const [draft, setDraft] = useState(NO_FILTERS);
  const [applied, setApplied] = useState(NO_FILTERS);
  const [page, setPage] = useState(1);
  // Bumped by Refresh / Retry, so the stats tiles are re-read together with the page.
  const [statsTick, setStatsTick] = useState(0);
  const { data: result, totalForCurrent, loading, loadFailed, reload, retry } = usePagedList({
    path: "/super-admin/audit-logs", filters: applied, page, setPage, pageSize: PAGE_SIZE,
  }); // result: { total, items }

  useEffect(() => {
    let cancelled = false;
    api.get("/super-admin/audit-logs/stats")
      .then((r) => { if (!cancelled) setStats(r.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [statsTick]);

  useEffect(() => {
    let cancelled = false;
    api.get("/super-admin/audit-logs/facets")
      .then((r) => {
        if (cancelled) return;
        const { actions, target_types: targetTypes } = r.data || {};
        // A 200 that is not the shape (a production proxy answering index.html) is a failure.
        if (!Array.isArray(actions) || !Array.isArray(targetTypes)) throw new Error("bad facets payload");
        setFacets({ actions, target_types: targetTypes });
        setFacetsFailed(false);
      })
      .catch(() => { if (!cancelled) setFacetsFailed(true); });
    return () => { cancelled = true; };
  }, [statsTick]);

  const setField = (key) => (e) => setDraft((d) => ({ ...d, [key]: e.target.value }));
  // The server refuses an inverted range, so a draft with one cannot be applied.
  const rangeInvalid = isRangeInvalid(draft.from, draft.to);
  const submit = (e) => {
    e.preventDefault();
    if (rangeInvalid) return;
    // The actor is free text and gets the search rule (a control character is a 422 no Retry can
    // fix); the dropdown values come from the server, the practice from the picker, the dates
    // from date inputs.
    setApplied({ ...draft, actor: normalizeSearch(draft.actor) });
    setPage(1);
  };
  const refresh = () => { reload(); setStatsTick((n) => n + 1); };
  const clearFilters = () => { setDraft(NO_FILTERS); setApplied(NO_FILTERS); setPage(1); };
  const events = result ? result.items : [];
  const hasAppliedFilter = Object.values(applied).some(Boolean);
  const actionOptions = withDrafted(facets ? facets.actions : [], draft.action);
  const targetOptions = withDrafted(facets ? facets.target_types : [], draft.target_type);

  return (
    <div data-testid="sa-audit">
      <div className="flex items-start justify-between mb-8">
        <div>
          <h1 className="font-sans font-semibold text-5xl mb-2 text-slate-800">{t("audit.title")}</h1>
          <p className="text-slate-500">{t("audit.subtitle")}</p>
        </div>
        <Button onClick={refresh} variant="outline" className="border-slate-200 text-slate-800/70" data-testid="audit-refresh">
          <RefreshCw className="w-4 h-4 mr-2" /> {t("audit.reload")}
        </Button>
      </div>

      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="rounded-2xl bg-white border border-slate-200 p-5">
            <div className="text-xs text-slate-400 uppercase tracking-wider mb-1">{t("audit.totalEvents")}</div>
            <div className="font-sol font-semibold text-3xl text-amber-600">{stats.total}</div>
          </div>
          {(stats.by_action || []).slice(0, 3).map((b) => (
            <div key={b.action} className="rounded-2xl bg-white border border-slate-200 p-5">
              <div className="text-xs text-slate-400 uppercase tracking-wider mb-1">{auditActionLabel(t, b.action)}</div>
              <div className="font-sol font-semibold text-3xl">{b.count}</div>
            </div>
          ))}
        </div>
      )}

      <form onSubmit={submit} className="rounded-2xl bg-white border border-slate-200 p-4 mb-4 flex flex-wrap items-center gap-3" data-testid="audit-filters">
        <Filter className="w-4 h-4 text-slate-400" />
        <Input
          placeholder={t("audit.phActor")} value={draft.actor} onChange={setField("actor")} maxLength={FILTER_MAX_LENGTH}
          className="bg-slate-50 border-slate-200 max-w-xs" data-testid="filter-actor"
        />
        <select
          value={draft.action} onChange={setField("action")} className={SELECT_CLASS}
          aria-label={t("audit.filterAction")} data-testid="filter-action"
        >
          <option value="">{t("audit.allActions")}</option>
          {actionOptions.map((v) => <option key={v} value={v}>{auditActionLabel(t, v)}</option>)}
        </select>
        <select
          value={draft.target_type} onChange={setField("target_type")} className={SELECT_CLASS}
          aria-label={t("audit.filterTarget")} data-testid="filter-target"
        >
          <option value="">{t("audit.allTargets")}</option>
          {targetOptions.map((v) => <option key={v} value={v}>{auditTargetLabel(t, v)}</option>)}
        </select>
        {showPracticePicker && (
          <TenantPicker
            value={draft.tenant_id}
            onChange={(id) => setDraft((d) => ({ ...d, tenant_id: id }))}
            allowAll
            className="w-64"
            testId="filter-tenant"
          />
        )}
        <DateRangeFilter
          from={draft.from}
          to={draft.to}
          onFromChange={(v) => setDraft((d) => ({ ...d, from: v }))}
          onToChange={(v) => setDraft((d) => ({ ...d, to: v }))}
          testIds={{ from: "filter-from", to: "filter-to", error: "audit-range-error" }}
        />
        <Button type="submit" disabled={rangeInvalid} className="bg-amber-300 text-black hover:bg-amber-200" data-testid="apply-filters">
          {t("audit.apply")}
        </Button>
      </form>

      {facetsFailed && (
        <p className="text-sm text-rose-600 mb-4" role="status" data-testid="audit-facets-error">{t("audit.facetsFailed")}</p>
      )}

      {/* Only a total read for the filters on screen is printed (a page turn keeps it). */}
      {totalForCurrent !== null && (
        <p className="text-sm text-slate-500 mb-3" data-testid="audit-count">{t("audit.countLine", { n: totalForCurrent })}</p>
      )}

      <LoadFailure
        message={loadFailed ? t("audit.loadFailed") : ""}
        onRetry={() => { retry(); setStatsTick((n) => n + 1); }}
        retryLabel={t("sal.retry")}
        testId="audit-load-error"
        className="mb-4"
      />

      {loading ? (
        <Loader2 className="w-6 h-6 animate-spin text-amber-600" />
      ) : !loadFailed && result && (
        <div className="rounded-2xl bg-white border border-slate-200 overflow-hidden">
          <div className="divide-y divide-white/5">
            {events.map((e) => (
              <div key={e.event_id} className="px-6 py-4 flex items-start gap-4 hover:bg-white" data-testid={`audit-row-${e.event_id}`}>
                <div className="w-9 h-9 rounded-lg bg-amber-100 flex items-center justify-center text-base flex-shrink-0">
                  {ACTION_ICONS[e.action] || <History className="w-4 h-4 text-amber-600" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <code title={e.action} className="text-xs font-medium text-amber-600">{auditActionLabel(t, e.action)}</code>
                    {e.target_type && (
                      <span
                        title={`${e.target_type}/${e.target_id}`}
                        className="text-[10px] uppercase tracking-wider text-slate-400 bg-white shadow-sm rounded px-2 py-0.5 break-all"
                        data-testid={`audit-target-${e.event_id}`}
                      >
                        {`${auditTargetLabel(t, e.target_type)}/${e.target_id}`}
                      </span>
                    )}
                    {e.tenant && <PracticeChip event={e} label={t("audit.practice")} deletedLabel={t("audit.practiceDeleted")} link={mayOpen(`/super-admin/tenants/${e.tenant.tenant_id}`, operator)} />}
                  </div>
                  <div className="text-xs text-slate-500 mt-1" data-testid={`audit-actor-${e.event_id}`}>
                    {t("audit.by")} <span className="text-slate-700">{e.actor_email}</span>
                    {e.actor_role && <> · {t("audit.role")} <span title={e.actor_role} className="text-slate-600">{auditRoleLabel(t, e.actor_role)}</span></>}
                  </div>
                  {e.action === "practice.write" && <WriteLine event={e} t={t} />}
                  {(e.before || e.after) && (
                    <details className="mt-2 text-[10px] text-slate-400">
                      <summary className="cursor-pointer hover:text-slate-500">{t("audit.diffDetails")}</summary>
                      <div className="grid md:grid-cols-2 gap-2 mt-2">
                        {e.before && <pre className="bg-rose-500/5 border border-rose-500/15 rounded p-2 overflow-x-auto"><span className="text-rose-400">- {t("audit.diffBefore")}</span>{"\n"}{JSON.stringify(e.before, null, 2)}</pre>}
                        {e.after && <pre className="bg-emerald-400/5 border border-emerald-400/15 rounded p-2 overflow-x-auto"><span className="text-emerald-400">+ {t("audit.diffAfter")}</span>{"\n"}{JSON.stringify(e.after, null, 2)}</pre>}
                      </div>
                    </details>
                  )}
                </div>
                <div className="text-xs text-slate-400 whitespace-nowrap">{formatDateTime(e.at, lang)}</div>
              </div>
            ))}
            {events.length === 0 && (
              <div className="px-6 py-10 text-center text-slate-400 text-sm">
                {hasAppliedFilter ? (
                  <>
                    <p>{t("audit.emptyFiltered")}</p>
                    <Button type="button" variant="outline" onClick={clearFilters} className="mt-3 border-slate-200 text-slate-800/70" data-testid="audit-clear-filters">
                      {t("audit.clearFilters")}
                    </Button>
                  </>
                ) : t("audit.empty")}
              </div>
            )}
          </div>
          {result.total > PAGE_SIZE && (
            <Pager
              page={page}
              pageSize={PAGE_SIZE}
              total={result.total}
              shown={events.length}
              onPage={setPage}
              infoText={(v) => t("sal.pagerInfo", v)}
              prevLabel={t("sal.pagerPrev")}
              nextLabel={t("sal.pagerNext")}
              testId="audit-pager"
              tone="slate"
            />
          )}
        </div>
      )}
    </div>
  );
}

/** The practice an event concerns; a link to its 360 view while the practice exists and the operator may open it. */
function PracticeChip({ event, label, deletedLabel, link }) {
  const { tenant } = event;
  const deleted = tenant.slug === null && tenant.name === null;
  const name = tenantDisplayName(tenant);
  return (
    <span className="text-[10px] tracking-wider text-slate-500 bg-white shadow-sm rounded px-2 py-0.5" data-testid={`audit-practice-${event.event_id}`}>
      <span className="uppercase text-slate-400">{label}</span>{" "}
      {link && !deleted
        ? <Link to={`/super-admin/tenants/${tenant.tenant_id}`} className="text-amber-700 hover:underline">{name}</Link>
        : name}
      {deleted && <> · {deletedLabel}</>}
    </span>
  );
}

/**
 * The path a write acted on: the route template with the record parameters the row stored
 * (`metadata.path_params`) filled in, so a nested record whose own id is shared (lesson `L1`
 * of every student's enrollment) is still traceable. A placeholder the row does not carry
 * (the practice, shown by its chip) or a value that is not a string stays as the template.
 */
function filledRoute(route, pathParams) {
  if (!pathParams || typeof pathParams !== "object" || Array.isArray(pathParams)) return route;
  return route.replace(/\{(\w+)(?::\w+)?\}/g, (placeholder, name) => (
    Object.hasOwn(pathParams, name) && typeof pathParams[name] === "string" ? pathParams[name] : placeholder
  ));
}

/** What an operator wrote in a practice: the method's verb, the path it acted on (the route template in the title), the HTTP status. */
function WriteLine({ event, t }) {
  const { method, route, status, path_params: pathParams } = event.metadata || {};
  const verb = Object.hasOwn(METHOD_KEYS, method) ? t(METHOD_KEYS[method]) : method;
  return (
    <div className="text-xs text-slate-500 mt-1" data-testid={`audit-write-${event.event_id}`}>
      {verb} {route ? <code title={route} className="text-slate-600 break-all">{filledRoute(route, pathParams)}</code> : t("audit.routeUnknown")} · {t("audit.writeStatus", { status })}
    </div>
  );
}
